import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useSetUserDetails, useSetUserLogout } from "../../API_Master/MutateAPI";
import { notifyError } from "../../MethodMaster/JSXComponentMothod";
import Loader from "../../CommonComponents/Loader";
import { mainRouteInitial } from "../../Data/Constant";

const COOKIE_NAMES = [
  "Narendra_Modi_UserID",
  // "Narendra_Modi_UserName",
  // "Mukesh_Patel_UserMobile",
  "Narendra_Modi_LoginFrom",
  "Narendra_Modi_UserUniqueID",
  "Narendra_Modi_SessionExpireTime",
  "Narendra_Modi_Village_ID",
  "isLoggedIn",
];

const parseSessionExpireTime = (value) => {
  if (!value) return NaN;
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;

  const numericValue = Number(value);
  if (!Number.isNaN(numericValue)) return numericValue < 1e12 ? numericValue * 1000 : numericValue;

  // Safari does not reliably parse SQL-style dates containing a space.
  let strValue = String(value).trim().replace(" ", "T");

  // The API sends local time but incorrectly appends 'Z' (making it UTC).
  // Stripping 'Z' ensures the browser parses it in the user's local timezone.
  if (strValue.endsWith("Z") || strValue.endsWith("z")) {
    strValue = strValue.slice(0, -1);
  }

  return new Date(strValue).getTime();
};

const ProtectedRoute = ({ children }) => {
  // Old URL parameters: const { id, MobileNo } = useParams();
  const { loginFrom: routeLoginFrom, userUniqueID: routeUserUniqueID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: getUserDetails, isPending } = useSetUserDetails();
  const { mutate: setLogOutUser, isPending: isLoggingOut } = useSetUserLogout();
  const [loading, setLoading] = useState(true);
  const [isVillagePresent, setIsVillagePresent] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const hasFetchedRef = useRef(false);
  const dashboardRoute = `/${mainRouteInitial}/Dashboard`;
  const currentLoginFrom = (routeLoginFrom || Cookies.get("Narendra_Modi_LoginFrom") || "").trim();
  const isAppLogin = currentLoginFrom.toLowerCase() === "app";

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const clearSession = () => COOKIE_NAMES.forEach((name) => Cookies.remove(name));
    const expireSession = (showToast = true) => {
      clearSession();
      setSessionExpired(true);
      if (showToast) notifyError("Session expired. Please log in again.", false);
    };

    // --- Static login check (mobile number 9978982479) ---
    const isStaticLoggedIn = Cookies.get("isLoggedIn") === "true" || localStorage.getItem("isLoggedIn") === "true";
    if (isStaticLoggedIn) {
      setIsVillagePresent(true);
      setLoading(false);
      return;
    }

    // Old values:
    // const userId = id || Cookies.get("Narendra_Modi_UserID");
    // const mobile = MobileNo || Cookies.get("Mukesh_Patel_UserMobile");
    const loginFrom = routeLoginFrom || Cookies.get("Narendra_Modi_LoginFrom");
    const userUniqueID = routeUserUniqueID || Cookies.get("Narendra_Modi_UserUniqueID");
    const savedExpiry = Cookies.get("Narendra_Modi_SessionExpireTime");

    console.log("Checking session on mount:", {
      savedExpiry,
      parsedExpiry: savedExpiry ? parseSessionExpireTime(savedExpiry) : null,
      currentTime: Date.now(),
      isExpired: savedExpiry ? parseSessionExpireTime(savedExpiry) <= Date.now() : false
    });

    if (savedExpiry && parseSessionExpireTime(savedExpiry) <= Date.now()) {
      console.log("Session expired based on saved cookie!");
      expireSession();
      setLoading(false);
      return;
    }

    if (!loginFrom || !userUniqueID) {
      navigate(`/${mainRouteInitial}/Login`, { replace: true });
      setLoading(false);
      return;
    }

    getUserDetails(
      // Old payload: { UserID: userId, MobileNo: mobile }
      { UserUniqueID: userUniqueID, LoginFrom: loginFrom },
      {
        onSuccess: (res) => {
          if (!res?.status || !res?.data?.length) {
            expireSession(false);
            return;
          }

          const user = res.data[0];
          const sessionExpireTime = user?.SessionExpireTime || res?.SessionExpireTime;
          const expiryTimestamp = parseSessionExpireTime(sessionExpireTime);

          console.log("Checking API session expiry:", {
            sessionExpireTime,
            expiryTimestamp,
            currentTime: Date.now(),
            isExpired: sessionExpireTime && !Number.isNaN(expiryTimestamp) && expiryTimestamp <= Date.now()
          });

          // A new session can temporarily return null. Expire only when the API
          // supplies a valid time and that time has already passed.
          if (sessionExpireTime && !Number.isNaN(expiryTimestamp) && expiryTimestamp <= Date.now()) {
            console.log("Session expired based on API response!");
            expireSession();
            return;
          }

          Cookies.set("Narendra_Modi_LoginFrom", loginFrom);
          Cookies.set("Narendra_Modi_UserUniqueID", userUniqueID);
          if (sessionExpireTime && !Number.isNaN(expiryTimestamp)) {
            Cookies.set("Narendra_Modi_SessionExpireTime", String(sessionExpireTime));
            setSessionExpiresAt(expiryTimestamp);
          } else {
            Cookies.remove("Narendra_Modi_SessionExpireTime");
            setSessionExpiresAt(null);
          }
          Cookies.set("Narendra_Modi_UserID", user.UserID || "");
          // Cookies.set("Narendra_Modi_UserName", user.UserName || "");
          // Cookies.set("Mukesh_Patel_UserMobile", user.MobileNo || "");
          Cookies.set("isLoggedIn", "true");

          const villageDetails = user?.ElectionUserAssignVillageDetail;
          const villages = Array.isArray(villageDetails) ? villageDetails : [];
          const villageDataWasReturned = Array.isArray(villageDetails);
          Cookies.set("Narendra_Modi_Village_ID", villages.map((item) => item?.VillageName).filter(Boolean).join(","));

          // V1 can return null because village details are not part of its login
          // response. Only reject when the API explicitly returns an empty list.
          const hasAccess = !villageDataWasReturned || villages.length > 0;
          setIsVillagePresent(hasAccess);

          if (villageDataWasReturned && !villages.length) {
            setLogOutUser(
              { UserID: user.UserID || "", MobileNo: user.MobileNo || "" },
              {
                onSuccess: () => {
                  clearSession();
                  notifyError("Village isn't assigned to this user", false);
                },
                onError: (err) => notifyError(err?.message || "Logout failed", false),
              }
            );
            return;
          }

          // Old behavior kept the login values in the URL after authentication:
          // navigate(`/NarendraModi/UserTask/${loginFrom}/${userUniqueID}`, { replace: true });
          // Once both values are saved in cookies, hide them from the visible URL.
          if (String(loginFrom).toLowerCase() === "app") {
            navigate(dashboardRoute, { replace: true });
          } else if (routeLoginFrom || routeUserUniqueID) {
            const defaultRedirectRoute = String(loginFrom).toLowerCase() === "app" ? dashboardRoute : "/NarendraModi/UserTask";
            navigate(defaultRedirectRoute, { replace: true });
          }

        },
        onError: (err) => {
          notifyError(err?.response?.data?.message || err?.message || "Something went wrong", false);
          expireSession(false);
        },
        onSettled: () => setLoading(false),
      }
    );

  }, [getUserDetails, navigate, routeLoginFrom, routeUserUniqueID, setLogOutUser]);

  useEffect(() => {
    if (!sessionExpiresAt) return undefined;

    const timer = window.setTimeout(() => {
      COOKIE_NAMES.forEach((name) => Cookies.remove(name));
      setSessionExpired(true);
      notifyError("Session expired. Please log in again.", false);
    }, Math.min(sessionExpiresAt - Date.now(), 2147483647));

    return () => window.clearTimeout(timer);
  }, [sessionExpiresAt]);

  if (loading || isPending || isLoggingOut) return <Loader />;
  if (sessionExpired) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#dfe7ee",
          padding: "24px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            background: "#f6f6f6",
            borderRadius: "18px",
            boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
            padding: "36px 28px 26px",
            textAlign: "center",
            border: "1px solid rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              width: "90px",
              height: "90px",
              margin: "0 auto 18px",
              borderRadius: "50%",
              border: "4px solid #f15b5b",
              color: "#f15b5b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "50px",
              fontWeight: "700",
              background: "#f9f9f9",
            }}
          >
            !
          </div>

          <h3
            style={{
              margin: "0 0 10px",
              fontSize: "32px",
              fontWeight: "700",
              color: "rgb(31, 31, 31)",
              textAlign: "center",
            }}
          >
            Session Expired
          </h3>

          <p
            style={{
              margin: "0 0 22px",
              fontSize: "18px",
              color: "#4d4d4d",
              textAlign: "center",
              width: "100%",
            }}
          >
            Please log in again.
          </p>

          <div
            style={{
              borderTop: "1px solid rgba(0,0,0,0.08)",
              margin: "18px 0 22px",
            }}
          />

          <p
            style={{
              margin: "0 0 26px",
              fontSize: "15px",
              lineHeight: "1.5",
              color: "#4a4a4a",
              textAlign: "center",

            }}
          >
            Your session has expired. Please use a valid login link or sign in again from the portal.
          </p>

        </div>
      </div>
    );
  }
  if (!isVillagePresent) return null;

  return children;
};

export default ProtectedRoute;
